import { useGameStore } from '@/store/gameStore'
import { SPONSORSHIP_OFFERS, Sponsorship } from '@/config/sponsorshipConfig'
import { getDivisionOrNone, divisionGateLabel } from '@/config/federationConfig'
import { SectionDeck, IconTile, StatStrip, EmptyStateRich } from '../components/CourtVision'
import { Handshake, Store, CupSoda, Shirt, Smartphone, Tv, Lock, Megaphone, type LucideIcon } from 'lucide-react'

const SPONSOR_TYPE_ICONS: Record<string, LucideIcon> = {
  equipment: Store,
  beverage: CupSoda,
  apparel: Shirt,
  tech: Smartphone,
  media: Tv,
}

export function SponsorshipsTabContent() {
  const { reputation, courts, month, addMoney, activeSponsorships, addActiveSponsorship, league } = useGameStore()
  const currentDivision = getDivisionOrNone(league)

  const canAccept = (sponsor: Sponsorship): { can: boolean; reason?: string } => {
    if (activeSponsorships.some(a => a.sponsorshipId === sponsor.id)) return { can: false, reason: 'Ya activo' }
    const activeSponsorTypes = activeSponsorships
      .map(a => SPONSORSHIP_OFFERS.find(s => s.id === a.sponsorshipId)?.type)
      .filter(Boolean)
    if (activeSponsorTypes.includes(sponsor.type)) return { can: false, reason: 'Tipo ya activo' }
    for (const req of sponsor.requirements) {
      if (req.type === 'reputation' && reputation < req.value)
        return { can: false, reason: `Rep. mínima ${req.value}` }
      if (req.type === 'courts' && courts.length < req.value)
        return { can: false, reason: `Mínimo ${req.value} pistas` }
      if (req.type === 'division' && currentDivision > req.value)
        return { can: false, reason: divisionGateLabel(req.value) }
    }
    return { can: true }
  }

  const acceptSponsor = (sponsor: Sponsorship) => {
    addActiveSponsorship({ sponsorshipId: sponsor.id, startMonth: month, endMonth: month + sponsor.duration })
    addMoney(sponsor.monthlyPayment, `Firma patrocinio: ${sponsor.name}`)
  }

  const monthlyIncome = activeSponsorships.reduce((sum, a) => {
    const sponsor = SPONSORSHIP_OFFERS.find(s => s.id === a.sponsorshipId)
    return sum + (sponsor ? sponsor.monthlyPayment : 0)
  }, 0)

  const pendingOffers = SPONSORSHIP_OFFERS.filter(s => !activeSponsorships.some(a => a.sponsorshipId === s.id))

  return (
    <div>
      {monthlyIncome > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <StatStrip
            label="Ingresos por patrocinio"
            value={<span className="money-positive">{monthlyIncome.toLocaleString('es-ES')}€/mes</span>}
            suffix={<IconTile icon={Handshake} tone="success" />}
          />
        </div>
      )}

      <SectionDeck title="Patrocinio activo" icon={Handshake} meta={activeSponsorships.length > 0 ? `${activeSponsorships.length} contrato${activeSponsorships.length === 1 ? '' : 's'}` : undefined}>
        {activeSponsorships.length === 0 ? (
          <EmptyStateRich
            icon={Handshake}
            title="Ninguna marca luce aún en tus pistas"
            sub="Firma una oferta y empieza a cobrar cada mes por llevar su logo"
          />
        ) : (
          <div className="space-y-2">
            {activeSponsorships.map(active => {
              const sponsor = SPONSORSHIP_OFFERS.find(s => s.id === active.sponsorshipId)
              if (!sponsor) return null
              const SponsorIcon = SPONSOR_TYPE_ICONS[sponsor.type] ?? Handshake
              return (
                <div key={active.sponsorshipId} className="row-tile">
                  <IconTile icon={SponsorIcon} tone="success" />
                  <div className="row-tile-main">
                    <div className="row-tile-title">{sponsor.name}</div>
                    <div className="row-tile-sub">{active.endMonth - month} meses restantes de contrato</div>
                  </div>
                  <div className="row-tile-side">
                    <span className="money-positive" style={{ fontWeight: 700 }}>
                      +{sponsor.monthlyPayment.toLocaleString('es-ES')}€/mes
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SectionDeck>

      <SectionDeck title="Ofertas" icon={Megaphone} meta={`${pendingOffers.length} sobre la mesa`}>
        <div className="space-y-2">
          {pendingOffers.map(sponsor => {
            const { can, reason } = canAccept(sponsor)
            const SponsorIcon = SPONSOR_TYPE_ICONS[sponsor.type] ?? Handshake

            if (!can) {
              return (
                <div key={sponsor.id} className="row-tile" style={{ opacity: 0.6 }}>
                  <IconTile icon={SponsorIcon} tone="muted" />
                  <div className="row-tile-main">
                    <div className="row-tile-title">{sponsor.name}</div>
                    <div className="row-tile-sub">
                      {sponsor.monthlyPayment.toLocaleString('es-ES')}€/mes · {sponsor.duration} meses
                    </div>
                  </div>
                  <span className="pill pill-warning"><Lock size={10} /> {reason}</span>
                </div>
              )
            }

            return (
              <div key={sponsor.id} className="row-tile">
                <IconTile icon={SponsorIcon} />
                <div className="row-tile-main">
                  <div className="row-tile-title">{sponsor.name}</div>
                  <div className="row-tile-sub">
                    {sponsor.bonuses.map(b => b.description).join(' · ')}
                  </div>
                </div>
                <div className="row-tile-side">
                  <span className="money-positive" style={{ fontWeight: 700 }}>
                    +{sponsor.monthlyPayment.toLocaleString('es-ES')}€/mes
                  </span>
                  <span className="meta-text">{sponsor.duration} meses</span>
                  <button
                    onClick={() => acceptSponsor(sponsor)}
                    className="btn btn-success"
                    style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }}
                  >Firmar</button>
                </div>
              </div>
            )
          })}
        </div>
      </SectionDeck>
    </div>
  )
}
